import { reflect } from '@effector/reflect';
import { NumberInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { WalletIcon } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { WalletWithAccountId } from 'shared/api';
import { translated, $t } from 'shared/lib';
import { createModalForm, ModalFormButtonProps, ModalFormProps } from 'shared/ui';
import { WithdrawWalletModel } from '../../model';

export const createWithdrawWalletModal = (model: WithdrawWalletModel) => {
	const ModalForm = createModalForm(model);

	const AmountField = reflect({
		view: NumberInput,
		bind: {
			'data-test-id': 'withdraw-wallet-amount-input',
			value: model.form.fields.amount.$value,
			onChange: (e) => model.form.fields.amount.change(`${e}`),
			label: translated('withdraw-wallet.amount'),
			placeholder: '250.00',
			rightSection: model.$modalData.map((wallet) => wallet?.currency ?? null),
			errorProps: { 'data-test-id': 'withdraw-wallet-amount-input-error' },
			error: combine(
				[model.form.fields.amount.$errors, $t],
				([errors, t]) =>
					errors.length > 0 &&
					t(`errors:validation.${errors[0]}`, {
						fieldName: t('withdraw-wallet.amount'),
					}),
			),
		},
	});

	const WithdrawWalletModal = forwardRef<HTMLDivElement, ModalFormProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const pending = useUnit(model.$pending);

		return (
			<ModalForm
				ref={ref}
				title={t('withdraw-wallet.title')}
				submitButtonProps={{
					children: t('withdraw-wallet.withdraw'),
					loading: pending,
				}}
				cancelButtonProps={{
					children: t('withdraw-wallet.cancel'),
				}}
				{...props}
			>
				<AmountField />
			</ModalForm>
		);
	});

	const WithdrawWalletModalButton = forwardRef<
		HTMLButtonElement,
		ModalFormButtonProps<WalletWithAccountId>
	>(({ modalData, ...props }, ref) => {
		const { t } = useTranslation();
		const userAccountId = useUnit(viewerModel.$accountId);

		return (
			<ModalForm.Button
				ref={ref}
				data-test-id='withdraw-wallet-button'
				leftSection={<WalletIcon />}
				disabled={modalData.isLocked || modalData.accountId === userAccountId}
				modalData={modalData}
				{...props}
			>
				{t('withdraw-wallet.withdraw')}
			</ModalForm.Button>
		);
	});

	return Object.assign(WithdrawWalletModal, {
		Button: WithdrawWalletModalButton,
	});
};
